import axios from "axios";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import type {PersonalizationOffer} from "./personalizationOffer";
import type {PersonalizationAssetRef} from "./personalizationDraft";
import type {PersonalizationFontKey} from "./personalizationFont";

/**
 * The per-transfer personalization endpoints (api kitecorp/transferify-api-service#461).
 *
 * Every call here is authenticated except the asset leaf, which the recipient page loads as a plain
 * image URL and this module therefore never fetches. `transferId` is the ENCODED capability — the same
 * token `/transfer/uploads/{id}/complete` takes — so the ui addresses these with the one identifier the
 * creation response already handed it, and never with a database id it would have to be told separately.
 */

/*
 * Compile-time bindings to the generated spec (see api/schemaAssert.ts). A backend rename, retype or
 * nullability change fails `tsc -b` naming the field, instead of surfacing as an undefined at runtime.
 */
export type _PersonalizationOffer = AssertNoSpecDrift<SpecDrift<PersonalizationOffer, Schemas["PersonalizationOffer"]>>;
export type _PersonalizationAsset = AssertNoSpecDrift<SpecDrift<PersonalizationAssetRef, Schemas["PersonalizationAsset"]>>;
/*
 * The font enum, bound in the direction the ordinary drift check cannot see (ui#1814).
 *
 * `SpecDrift` is deliberately lenient about NARROWING — a hand-written union may be narrower than the
 * spec's — which is right for every other field and exactly wrong for a closed catalogue: a face the api
 * ADDS would pass every assertion above while `offeredFonts` silently dropped it from the picker, and
 * the only symptom would be an option nobody could choose. `Exclude` inverts the question: any published
 * face this build has no family for is a compile error naming that face.
 */
export type _PersonalizationFontCoversSpec = AssertNoSpecDrift<
	Exclude<NonNullable<Schemas["PersonalizationDraft"]["font"]>, PersonalizationFontKey>>;

export class PersonalizationApi {
	/**
	 * `GET /transfer/personalization/offer` — whether the sender's plan includes personalization, and the
	 * catalogue the composer renders from. Nothing is sent.
	 */
	static async getOffer(): Promise<PersonalizationOffer> {
		const {data} = await axios.get<PersonalizationOffer>("/transfer/personalization/offer");
		return data;
	}

	/** `POST /transfer/personalization/assets` — one backdrop image. Multipart part name `image`. */
	static async uploadImage(image: File): Promise<PersonalizationAssetRef> {
		return upload("/transfer/personalization/assets", image);
	}

	/** `POST /transfer/personalization/logo` — the transfer's logo. Multipart part name `image`. */
	static async uploadLogo(image: File): Promise<PersonalizationAssetRef> {
		return upload("/transfer/personalization/logo", image);
	}
}

/**
 * Both asset endpoints take the same multipart part name (`image`) and answer the same record, so the
 * one difference between them — which ceiling the api applies — stays entirely on the api side where it
 * belongs. Two nearly identical bodies is how the logo path quietly keeps posting under the wrong key.
 */
async function upload(path: string, image: File): Promise<PersonalizationAssetRef> {
	const body = new FormData();
	body.append("image", image);
	const {data} = await axios.post<PersonalizationAssetRef>(path, body, {
		headers: {"Content-Type": "multipart/form-data"},
	});
	return data;
}
